import { Component, signal, effect, AfterViewInit, OnDestroy, computed, DestroyRef, Injector } from '@angular/core';
import {
  CompilationsDataService,
  Compilation,
  TripLocation,
  TripSection,
  TripPOI,
  MapboxSpecificService,
  addLayersToMap,
  Pathway,
  PathwayEditEngine,
  QuestMetadata,
  Obelisk,
  isQuestMetadata,
  isObelisk,
  ExplorationService,
  QuestsCrudService
} from 'quest-atlas-angular-components';
import { ActivatedRoute, Router } from '@angular/router';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import * as mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { of, zip, Subject, BehaviorSubject, finalize } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MapEditorBase } from '../../../classes/map-editor-base';
import { environment } from '../../../../environments/environment';
import { ModalButton } from '../../../common-components/modal/modal.component';
import { v4 as uuidv4 } from 'uuid';
import { readImageFromClipboard } from '../../../functions/file.functions';

// Available activity types for trips
export const ACTIVITY_TYPES = [
  { value: 'hike', icon: 'hike', label: 'hike' },
  { value: 'bike', icon: 'bike', label: 'bike' },
  { value: 'run', icon: 'run', label: 'run' },
  { value: 'walk', icon: 'walk', label: 'walk' },
  { value: 'car', icon: 'car', label: 'car' },
  { value: 'kayak', icon: 'kayak', label: 'kayak' },
  { value: 'ski', icon: 'ski', label: 'ski' },
  { value: 'mountainBike', icon: 'biking-mountain', label: 'mountainBike' },
  { value: 'horse', icon: 'horse', label: 'horse' }
] as const;

@Component({
  selector: 'app-trip-editor',
  templateUrl: './trip-editor.component.html',
  styleUrl: './trip-editor.component.scss',
  standalone: false
})
export class TripEditorComponent extends MapEditorBase implements AfterViewInit, OnDestroy {
  readonly activityTypes = ACTIVITY_TYPES;

  tripId = signal<number | string | null>(null);
  isNew = computed(() => {
    return this.tripId() === null || this.tripId() === 'new';
  })
  trip = rxResource({
    params: () => ({ id: this.tripId() }),
    stream: ({ params }) => {
      return params.id && params.id !== 'new' ? this.compilationsService.getCompilation$(params.id as number) : of(null);
    }
  });

  tripForm: FormGroup;
  selectedLocationIndex = signal<number>(0);
  isSaving = signal(false);

  // POI management
  poiMarkers: Map<string, mapboxgl.Marker> = new Map();
  poiPreviewMarker?: mapboxgl.Marker;
  editingPOI?: TripPOI;
  editingPOILocationIndex?: number;
  showPOIModal = signal(false);
  availableMakiIcons: string[] = [];

  // Cluster management (Obelisks & Quests)
  clusterMarkers: Map<string, mapboxgl.Marker> = new Map();
  showClusterModal = signal(false);
  searchingClusters = signal(false);
  clusterSearchResults: (QuestMetadata | Obelisk)[] = [];
  clusterSearchQuery = '';
  private clusterSearchSubject$$ = new Subject<string>();

  // Image editing
  editingImageId?: string;
  editingImageDescription = '';
  editingImageLocationIndex?: number;
  editingImageSectionIndex?: number;
  editDescriptionModalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('cancel'),
      onClick: () => this.closeEditImageDescriptionDialog()
    },
    {
      type: 'primary',
      text: this.transloco.translate('save'),
      onClick: () => this.submitEditImageDescription()
    }
  ];


  private destroy$ = new Subject<void>();

  constructor(
    private compilationsService: CompilationsDataService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private explorationService: ExplorationService,
    private questsService: QuestsCrudService,
    private destroyRef: DestroyRef,
    private injector: Injector,
    fb: FormBuilder,
    mapboxSpecificService: MapboxSpecificService,
    transloco: TranslocoService,
    snackBar: MatSnackBar
  ) {
    super(mapboxSpecificService, snackBar, transloco, fb);

    // Initialize form
    this.tripForm = this.createTripForm();

    // Get trip ID from route
    this.activatedRoute.params.subscribe(params => {
      const id = params['id'] === 'new' ? null : Number(params['id']);
      this.tripId.set(id);
    });

    // Set up debounced cluster search
    this.clusterSearchSubject$$.pipe(
      debounceTime(300), // Wait 300ms after user stops typing
      distinctUntilChanged(), // Only trigger if the value changed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(query => {
      this.performClusterSearch(query);
    });

    // Load trip data when available
    effect(() => {
      const tripData = this.trip.value();
      if (tripData) {
        this.loadTripData(tripData);
      }
    });
  }

  createTripForm(): FormGroup {
    const form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      isPublic: [false],
      titleImageUrl: [''],
      activityTags: [[]],
      stats: this.fb.group({
        totalLength: [null],
        totalTime: [null],
        elevationGain: [null],
        elevationLoss: [null],
        pavedRoadsPercent: [null],
        unpavedRoadsPercent: [null]
      }),
      locations: this.fb.array([])
    });

    // For new trips, add the first location automatically
    const locationsArray = form.get('locations') as FormArray;
    locationsArray.push(this.createLocationForm());

    return form;
  }

  get locations(): FormArray {
    return this.tripForm.get('locations') as FormArray;
  }

  getLocationFormGroup(index: number): FormGroup {
    return this.locations.at(index) as FormGroup;
  }

  getLocationSections(locationIndex: number): FormArray {
    return this.locations.at(locationIndex).get('sections') as FormArray;
  }

  getSectionFormGroup(locationIndex: number, sectionIndex: number): FormGroup {
    return this.getLocationSections(locationIndex).at(sectionIndex) as FormGroup;
  }

  getSectionImages(locationIndex: number, sectionIndex: number): FormArray {
    return this.getLocationSections(locationIndex).at(sectionIndex).get('images') as FormArray;
  }

  getImageFormGroup(locationIndex: number, sectionIndex: number, imageIndex: number): FormGroup {
    return this.getSectionImages(locationIndex, sectionIndex).at(imageIndex) as FormGroup;
  }

  // Override createPathwayFormGroup to make path optional for new pathways
  protected override createPathwayFormGroup(pathway: Pathway): FormGroup {
    return this.fb.group({
      uuid: [pathway.uuid, [Validators.required]],
      name: [pathway.name],
      color: [pathway.color, [Validators.required]],
      vertices: [pathway.vertices || []],
      path: [pathway.path], // Make optional - will be populated when user draws
      mappedToRoads: [pathway.mappedToRoads || false],
      mapMappedPath: [pathway.mapMappedPath],
      mappedPathProfile: [pathway.mappedPathProfile || 'walking']
    });
  }

  createLocationForm(location?: TripLocation): FormGroup {
    return this.fb.group({
      name: [location?.name || '', Validators.required],
      sections: this.fb.array(
        location?.sections?.map(s => this.createSectionForm(s)) || [this.createSectionForm()]
      ),
      poi: [location?.poi || []],
      paths: this.fb.array(
        location?.paths?.map(p => this.createPathwayFormGroup(p)) || []
      ),
      clusters: [location?.clusters || []]
    });
  }

  createSectionForm(section?: TripSection): FormGroup {
    return this.fb.group({
      title: [section?.title || ''],
      subtitle: [section?.subtitle || ''],
      description: [section?.description || ''],
      images: this.fb.array(
        section?.images?.map(img => this.createImageForm(img)) || []
      )
    });
  }

  createImageForm(image?: { uuid?: string; url: string; caption: string }): FormGroup {
    return this.fb.group({
      uuid: [image?.uuid || uuidv4()],
      url: [image?.url || '', Validators.required],
      caption: [image?.caption || '']
    });
  }

  loadTripData(trip: Compilation): void {
    console.log('Loading trip data:', trip);

    this.tripForm.patchValue({
      name: trip.name,
      description: trip.description || '',
      isPublic: trip.isPublic,
      titleImageUrl: trip.tripConfig?.titleImageUrl || '',
      activityTags: trip.activityTags || [],
      stats: trip.tripConfig?.stats || {}
    });

    // Clear and rebuild locations array
    this.locations.clear();
    trip.tripConfig?.locations?.forEach(location => {
      this.locations.push(this.createLocationForm(location));
    });

    // If no locations, add an empty one
    if (this.locations.length === 0) {
      this.addLocation();
    }
  }

  addLocation(): void {
    console.log('addLocation called');

    // Add new location and switch to it
    this.locations.push(this.createLocationForm());
    this.selectedLocationIndex.set(this.locations.length - 1);

    // No cleanup needed - all pathways stay visible
  }

  removeLocation(index: number): void {
    if (this.locations.length > 1) {
      this.locations.removeAt(index);
      if (this.selectedLocationIndex() >= this.locations.length) {
        this.selectedLocationIndex.set(this.locations.length - 1);
      }
    }
  }

  addSection(locationIndex: number): void {
    const sections = this.getLocationSections(locationIndex);
    sections.push(this.createSectionForm());
  }

  removeSection(locationIndex: number, sectionIndex: number): void {
    const sections = this.getLocationSections(locationIndex);
    sections.removeAt(sectionIndex);
  }

  // Override initPathway to add debugging and ensure map is ready
  override initPathway(pathway: FormGroup, addLayers = true): any {
    console.log('initPathway called', {
      uuid: pathway.value,
      addLayers,
      mapExists: !!this.map,
      mapLoaded: this.mapLoaded
    });

    if (!this.map || !this.mapLoaded) {
      console.warn('Map not ready, skipping pathway initialization');
      return null;
    }

    try {
      // IMPORTANT: Add layers FIRST before creating PathwayEditEngine
      // This prevents the engine from trying to access sources that don't exist yet
      if (addLayers) {
        console.log('Adding pathway map layers first...');
        this.addPathwayMapLayers(pathway);
      }

      // Now create the PathwayEditEngine - sources will exist
      this.pathwaysStrokeColors.set(pathway.value.uuid, new BehaviorSubject<string>(pathway.value.color));

      pathway.valueChanges.subscribe((value) => {
        this.pathwaysStrokeColors.get(pathway.value.uuid).next(value.color);
      });

      console.log('Creating PathwayEditEngine...');
      const pE = new PathwayEditEngine(
        this.map,
        of({button: 'confirm'}),
        'pathway-' + pathway.value.uuid,
        this.pathwaysStrokeColors.get(pathway.value.uuid).asObservable(),
        pathway.value.path,
        () => {}
      );

      pE.onEdit$().subscribe(() => {
        const data = pE.getData();
        pathway.patchValue({
          path: data.cleanZone,
          vertices: data.vertices,
        });

        if (pathway.value.mappedToRoads) {
          this.loadMapMatching(pathway);
        }
      });

      pE.stopEdit$().subscribe(() => {
        this.currentPathway = undefined;
        pE.hideMarkers();
      });

      this.pathwaysEditEngines.set(pathway.value.uuid, pE);

      if (pathway.value.mappedToRoads) {
        this.map.once('styledata', () => {
          this.updateMappedPathwaysLayerData(pathway, pathway.value.mapMappedPath);
        });
      }

      if (addLayers) {
        this.pathwaysStrokeColorsUpdates(pathway);
      }

      console.log('Pathway initialized successfully');
      return pE;
    } catch (e) {
      console.error('Error initializing pathway:', e);
      // Try to clean up if initialization failed
      const uuid = pathway.value.uuid;
      if (this.pathwaysEditEngines.has(uuid)) {
        try {
          this.pathwaysEditEngines.get(uuid).destroy();
          this.pathwaysEditEngines.delete(uuid);
        } catch (cleanupError) {
          console.warn('Error during cleanup:', cleanupError);
        }
      }
      if (this.pathwaysStrokeColors.has(uuid)) {
        this.pathwaysStrokeColors.get(uuid).complete();
        this.pathwaysStrokeColors.delete(uuid);
      }
      return null;
    }
  }

  // Override startEditPathway to add debugging
  override startEditPathway(pathway: FormGroup): void {
    console.log('startEditPathway called', {
      pathwayUuid: pathway.value.uuid,
      engineExists: this.pathwaysEditEngines.has(pathway.value.uuid)
    });

    super.startEditPathway(pathway);

    console.log('Pathway editing started');
  }

  // Initialize all pathways from all locations
  private initializeAllPathways(): void {
    console.log('Initializing all pathways from all locations');

    this.locations.controls.forEach((location, locationIndex) => {
      const locationGroup = location as FormGroup;
      const pathsArray = locationGroup.get('paths') as FormArray;

      if (pathsArray && pathsArray.length > 0) {
        console.log(`Location ${locationIndex} has ${pathsArray.length} pathways`);
        pathsArray.controls.forEach((pathway) => {
          try {
            const pathwayUuid = pathway.value.uuid;
            // Only initialize if not already initialized
            if (!this.pathwaysEditEngines.has(pathwayUuid)) {
              console.log('Initializing pathway:', pathwayUuid);
              this.initPathway(pathway as FormGroup, true);
            }
          } catch (e) {
            console.error('Error initializing pathway:', e);
          }
        });
      }
    });
  }

  selectLocation(index: number): void {
    console.log('selectLocation called:', index);

    // Stop editing current pathway before switching
    this.stopEditCurrentPathway();

    this.selectedLocationIndex.set(index);

    // No cleanup - all pathways remain visible!
  }

  saveTrip(): void {
    if (this.tripForm.invalid) {
      this.tripForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const formValue = this.tripForm.value;

    // Collect all unique quests and obelisks from all locations
    const allQuests: QuestMetadata[] = [];
    const allObelisks: Obelisk[] = [];
    const questIds = new Set<number>();
    const obeliskUuids = new Set<string>();

    formValue.locations?.forEach((location: any) => {
      location.clusters?.forEach((cluster: any) => {
        if (isQuestMetadata(cluster)) {
          if (!questIds.has(cluster.id)) {
            questIds.add(cluster.id);
            allQuests.push(cluster);
          }
        } else if (isObelisk(cluster)) {
          if (!obeliskUuids.has(cluster.uuid)) {
            obeliskUuids.add(cluster.uuid);
            allObelisks.push(cluster);
          }
        }
      });
    });

    const tripData: Partial<Compilation> = {
      name: formValue.name,
      description: formValue.description,
      isPublic: formValue.isPublic,
      type: 'trip',
      quests: allQuests,
      obelisks: allObelisks,
      activityTags: formValue.activityTags || [],
      tripConfig: {
        titleImageUrl: formValue.titleImageUrl,
        poi: [],
        paths: [],
        locations: formValue.locations,
        stats: formValue.stats
      }
    };

    console.log('saving trip with data:', tripData);

    const tripId = this.tripId();
    const saveObservable = !this.isNew()
      ? this.compilationsService.updateTripConfig$(tripId as number, tripData)
      : this.compilationsService.createCompilation$(
          tripData.name!,
          tripData.description || '',
          tripData.isPublic!,
          tripData.type,
          tripData.activityTags,
          tripData.tripConfig,
          allQuests,
          allObelisks
        );

    saveObservable.subscribe({
      next: (result) => {
        this.isSaving.set(false);
        if (this.isNew()) {
          // Navigate to the new trip editor
          this.router.navigate(['/edit-trip', result.id]);
        } else {
          // Reload trip data
          this.trip.reload();
        }
      },
      error: (error) => {
        console.error('Error saving trip:', error);
        this.isSaving.set(false);
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/trips']);
  }

  toggleActivityTag(activityValue: string): void {
    const currentTags = this.tripForm.get('activityTags')?.value || [];
    const index = currentTags.indexOf(activityValue);

    let updatedTags: string[];
    if (index > -1) {
      // Remove tag
      updatedTags = currentTags.filter((tag: string) => tag !== activityValue);
    } else {
      // Add tag
      updatedTags = [...currentTags, activityValue];
    }

    this.tripForm.patchValue({ activityTags: updatedTags });
  }

  isActivityTagSelected(activityValue: string): boolean {
    const currentTags = this.tripForm.get('activityTags')?.value || [];
    return currentTags.includes(activityValue);
  }

  ngAfterViewInit(): void {
    effect(() => {
      const trip = this.trip.value();
      if (this.isNew()) {
        setTimeout(() => {
          this.initializeMap();
        }, 200)
      } else if (trip) {
        setTimeout(() => {
          this.initializeMap();
        }, 200)
      }
    }, {injector: this.injector});
  }

  // Implement abstract methods from MapEditorBase
  getPathways(): FormArray {
    const index = this.selectedLocationIndex();
    if (index >= 0 && index < this.locations.length) {
      const location = this.locations.at(index) as FormGroup;
      let pathsArray = location.get('paths') as FormArray;

      // If paths doesn't exist, create it
      if (!pathsArray) {
        pathsArray = this.fb.array([]);
        location.setControl('paths', pathsArray);
      }

      return pathsArray;
    }

    // Fallback: create temporary array (should not happen)
    return this.fb.array([]);
  }

  getPathwaysAsFormGroups(): FormGroup[] {
    return this.getPathways().controls as FormGroup[];
  }

  // Get all pathways from all locations grouped by location
  getAllPathwaysGroupedByLocation(): { locationIndex: number; locationName: string; pathways: FormGroup[] }[] {
    const result: { locationIndex: number; locationName: string; pathways: FormGroup[] }[] = [];

    this.locations.controls.forEach((location, index) => {
      const locationGroup = location as FormGroup;
      const pathsArray = locationGroup.get('paths') as FormArray;
      const locationName = locationGroup.get('name')?.value || `Location ${index + 1}`;

      if (pathsArray && pathsArray.length > 0) {
        result.push({
          locationIndex: index,
          locationName: locationName,
          pathways: pathsArray.controls as FormGroup[]
        });
      }
    });

    return result;
  }

  // Override addPathway to ensure map is loaded and proper initialization
  override addPathway(): void {
    console.log('addPathway called', {
      mapLoaded: this.mapLoaded,
      locationsCount: this.locations.length,
      selectedIndex: this.selectedLocationIndex()
    });

    if (!this.mapLoaded) {
      this.snackBar.open(
        this.transloco.translate('pleaseWaitForMapToLoad'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    // Ensure we have at least one location
    if (this.locations.length === 0) {
      this.addLocation();
    }

    const pathwaysBeforeAdd = this.getPathways().length;
    console.log('Pathways before add:', pathwaysBeforeAdd);

    // Create pathway manually to control editing start
    this.stopEditCurrentPathway();

    const pathway = this.createPathwayFormGroup({
      uuid: uuidv4(),
      name: '',
      color: '#ffa822',
      vertices: [],
      mappedPathProfile: 'walking',
    });

    this.currentPathway = pathway;
    this.getPathways().push(pathway);

    const pE = this.initPathway(pathway);

    setTimeout(() => {
      pathway.markAsTouched();
      pathway.markAsDirty();
      pathway.markAllAsTouched();
    }, 100);

    // Start editing immediately since map is already loaded
    console.log('Starting pathway editing...');
    setTimeout(() => {
      pE.startEditing();
      console.log('Pathway editing started');
    }, 100);

    const pathwaysAfterAdd = this.getPathways().length;
    console.log('Pathways after add:', pathwaysAfterAdd);
  }

  getComments(): FormArray {
    return this.fb.array([]); // Not used in trip editor yet
  }

  getCommentsAsFormGroups(): FormGroup[] {
    return [];
  }

  private initializeMap(): void {
    if (this.map) {
      return;
    }
    // @ts-ignore
    mapboxgl.config.ACCESS_TOKEN = environment.mapboxPublicToke;

    const lastLocation = this.getLastLocation();
    const centerAndZoom = lastLocation
      ? { zoom: 14, center: [lastLocation.lng, lastLocation.lat] }
      : { zoom: 2, center: [0, 0] };

    // @ts-ignore
    this.map = new mapboxgl.Map({
      container: 'map-div',
      style: 'mapbox://styles/mapbox/outdoors-v12',
      ...centerAndZoom
    });

    // Add map controls
    this.map.addControl(
      new MapboxGeocoder({
        accessToken: environment.mapboxPublicToke,
        mapboxgl: mapboxgl
      })
    );

    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.setLanguage([navigator.language, 'en']);

    this.map.on('load', () => {
      this.mapLoaded = true;
      addLayersToMap(this.map);

      // Initialize ALL pathways from ALL locations
      this.initializeAllPathways();

      // Initialize ALL POI markers from ALL locations
      this.initializeAllPOIMarkers();

      // Initialize ALL cluster markers from ALL locations
      this.initializeAllClusterMarkers();

      // Load available maki icons
      this.loadMakiIcons();
    });

    this.map.on('click', (ev) => {
      this.storeLastLocation({
        lng: this.map.getCenter().lng,
        lat: this.map.getCenter().lat
      });

      // If POI modal is open, set the POI location
      if (this.showPOIModal() && this.editingPOI) {
        this.editingPOI.coords = {
          lat: ev.lngLat.lat,
          lng: ev.lngLat.lng
        };

        // Update preview marker
        this.updatePOIPreviewMarker(ev.lngLat);
        return;
      }

      // If editing pathway, handle pathway click
      if (this.currentPathway) {
        this.pathwaysEditEngines.get(this.currentPathway.value.uuid).clickOnMap(ev.lngLat);
        return;
      }
    });

    this.map.on('mousemove', (ev: any) => {
      if (this.currentPathway) {
        this.pathwaysEditEngines.get(this.currentPathway.value.uuid).hoverOnMap(ev.lngLat);
      }
    });
  }

  // Map-related methods
  getMapCenter(): [number, number] | null {
    const location = this.locations.at(this.selectedLocationIndex())?.value;
    if (location?.paths && location.paths.length > 0) {
      const firstPath = location.paths[0];
      if (firstPath.coordinates && firstPath.coordinates.length > 0) {
        const coord = firstPath.coordinates[0];
        return [coord.lng, coord.lat];
      }
    }
    return null;
  }

  // Image editing methods
  addImageToSection(locationIndex: number, sectionIndex: number): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png, image/jpeg';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        this.processImageFile(file, locationIndex, sectionIndex);
      }
    };
    input.click();
  }

  private processImageFile(file: File, locationIndex: number, sectionIndex: number): void {
    const reader = new FileReader();

    reader.onload = () => {
      const images = this.getSectionImages(locationIndex, sectionIndex);
      images.push(this.createImageForm({
        uuid: uuidv4(),
        url: reader.result as string,
        caption: ''
      }));
    };

    reader.readAsDataURL(file);
  }

  pasteImageFromClipboard(locationIndex: number, sectionIndex: number): void {
    readImageFromClipboard().subscribe((blob: Blob) => {
      if (blob) {
        const reader = new FileReader();
        reader.onload = () => {
          const images = this.getSectionImages(locationIndex, sectionIndex);
          images.push(this.createImageForm({
            uuid: uuidv4(),
            url: reader.result as string,
            caption: ''
          }));
        };
        reader.readAsDataURL(blob);
      } else {
        this.snackBar.open(
          this.transloco.translate('noImageInClipboard'),
          this.transloco.translate('close'),
          {
            verticalPosition: 'bottom',
            duration: 3000
          }
        );
      }
    });
  }

  openEditImageDescriptionDialog(locationIndex: number, sectionIndex: number, imageIndex: number): void {
    const image = this.getSectionImages(locationIndex, sectionIndex).at(imageIndex).value;
    this.editingImageId = image.uuid;
    this.editingImageDescription = image.caption || '';
    this.editingImageLocationIndex = locationIndex;
    this.editingImageSectionIndex = sectionIndex;
  }

  closeEditImageDescriptionDialog(): void {
    this.editingImageId = undefined;
    this.editingImageDescription = '';
    this.editingImageLocationIndex = undefined;
    this.editingImageSectionIndex = undefined;
  }

  pasteTitleImageFromClipboard(): void {
    readImageFromClipboard().subscribe((blob: Blob) => {
      if (blob) {
        const reader = new FileReader();
        reader.onload = () => {
          this.tripForm.patchValue({
            titleImageUrl: reader.result as string
          });
        };
        reader.readAsDataURL(blob);
      } else {
        this.snackBar.open(
          this.transloco.translate('noImageInClipboard'),
          this.transloco.translate('close'),
          {
            verticalPosition: 'bottom',
            duration: 3000
          }
        );
      }
    });
  }

  uploadTitleImageFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.tripForm.patchValue({
          titleImageUrl: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  }

  removeTitleImage(): void {
    this.tripForm.patchValue({
      titleImageUrl: ''
    });
  }

  submitEditImageDescription(): void {
    if (this.editingImageLocationIndex !== undefined &&
        this.editingImageSectionIndex !== undefined &&
        this.editingImageId) {
      const images = this.getSectionImages(this.editingImageLocationIndex, this.editingImageSectionIndex);
      const imageIndex = images.controls.findIndex(img => img.value.uuid === this.editingImageId);

      if (imageIndex !== -1) {
        images.at(imageIndex).patchValue({
          caption: this.editingImageDescription
        });
      }
    }

    this.closeEditImageDescriptionDialog();
  }

  // POI Management Methods
  getLocationPOIs(locationIndex: number): TripPOI[] {
    const location = this.getLocationFormGroup(locationIndex);
    return location?.get('poi')?.value || [];
  }

  addPOI(locationIndex: number): void {
    if (!this.mapLoaded || !this.map) {
      this.snackBar.open(
        this.transloco.translate('pleaseWaitForMapToLoad'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    const center = this.map.getCenter();
    this.editingPOI = {
      uuid: uuidv4(),
      name: '',
      description: '',
      website: '',
      icon: 'marker',
      coords: { lat: center.lat, lng: center.lng }
    };
    this.editingPOILocationIndex = locationIndex;
    this.showPOIModal.set(true);

    // Show preview marker at current location
    this.updatePOIPreviewMarker({ lat: center.lat, lng: center.lng });
  }

  editPOI(locationIndex: number, poi: TripPOI): void {
    this.editingPOI = { ...poi };
    this.editingPOILocationIndex = locationIndex;
    this.showPOIModal.set(true);

    // Show preview marker at POI location
    this.updatePOIPreviewMarker(poi.coords);
  }

  removePOI(locationIndex: number, poiUuid: string): void {
    const location = this.getLocationFormGroup(locationIndex);
    const pois = this.getLocationPOIs(locationIndex);
    const updatedPOIs = pois.filter(p => p.uuid !== poiUuid);
    location.patchValue({ poi: updatedPOIs });

    // Remove marker from map
    const marker = this.poiMarkers.get(poiUuid);
    if (marker) {
      marker.remove();
      this.poiMarkers.delete(poiUuid);
    }
  }

  closePOIModal(): void {
    this.showPOIModal.set(false);
    this.editingPOI = undefined;
    this.editingPOILocationIndex = undefined;

    // Remove preview marker
    this.removePOIPreviewMarker();
  }

  selectPOIIcon(iconName: string): void {
    if (!this.editingPOI) return;

    this.editingPOI.icon = iconName;

    // Update preview marker with new icon
    if (this.editingPOI.coords) {
      this.updatePOIPreviewMarker(this.editingPOI.coords);
    }
  }

  submitPOI(): void {
    if (!this.editingPOI || this.editingPOILocationIndex === undefined) {
      return;
    }

    if (!this.editingPOI.name) {
      this.snackBar.open(
        this.transloco.translate('pleaseEnterNameForPOI'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    const location = this.getLocationFormGroup(this.editingPOILocationIndex);
    const pois = this.getLocationPOIs(this.editingPOILocationIndex);

    const existingIndex = pois.findIndex(p => p.uuid === this.editingPOI!.uuid);
    let updatedPOIs: TripPOI[];

    if (existingIndex >= 0) {
      // Update existing POI
      updatedPOIs = [...pois];
      updatedPOIs[existingIndex] = this.editingPOI;
    } else {
      // Add new POI
      updatedPOIs = [...pois, this.editingPOI];
    }

    location.patchValue({ poi: updatedPOIs });

    // Update or create marker on map
    this.updatePOIMarker(this.editingPOI);

    this.closePOIModal();
  }

  updatePOIMarker(poi: TripPOI): void {
    if (!this.map) return;

    // Remove existing marker if it exists
    const existingMarker = this.poiMarkers.get(poi.uuid!);
    if (existingMarker) {
      existingMarker.remove();
    }

    // Create custom marker element with maki icon
    const el = document.createElement('div');
    el.className = 'poi-marker';
    el.style.width = '30px';
    el.style.height = '30px';
    el.style.backgroundImage = `url('/assets/maki/${poi.icon}.svg')`;
    el.style.backgroundSize = '18px 18px';
    el.style.backgroundPosition = 'center';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';

    // Create popup
    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
      <div style="padding: 8px;">
        <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: 600;">${poi.name}</h3>
        ${poi.description ? `<p style="margin: 4px 0; font-size: 12px;">${poi.description}</p>` : ''}
        ${poi.website ? `<a href="${poi.website}" target="_blank" style="font-size: 12px;">Visit website</a>` : ''}
      </div>
    `);

    // Create marker
    const marker = new mapboxgl.Marker(el)
      .setLngLat([poi.coords.lng, poi.coords.lat])
      .setPopup(popup)
      .addTo(this.map);

    this.poiMarkers.set(poi.uuid!, marker);
  }

  // Update preview marker when selecting POI location
  updatePOIPreviewMarker(coords: { lat: number; lng: number }): void {
    if (!this.map) return;

    // Remove existing preview marker
    this.removePOIPreviewMarker();

    // Create preview marker element
    const el = document.createElement('div');
    el.className = 'poi-preview-marker';
    el.style.width = '40px';
    el.style.height = '40px';
    el.style.backgroundImage = `url('/assets/maki/${this.editingPOI?.icon || 'marker'}.svg')`;
    el.style.backgroundSize = '24px 24px';
    el.style.backgroundPosition = 'center';
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    el.style.animation = 'pulse 1.5s ease-in-out infinite';

    // Create preview marker
    this.poiPreviewMarker = new mapboxgl.Marker(el)
      .setLngLat([coords.lng, coords.lat])
      .addTo(this.map);
  }

  // Remove preview marker
  removePOIPreviewMarker(): void {
    if (this.poiPreviewMarker) {
      this.poiPreviewMarker.remove();
      this.poiPreviewMarker = undefined;
    }
  }

  // Initialize all POI markers on map load
  initializeAllPOIMarkers(): void {
    this.locations.controls.forEach((location, index) => {
      const pois = this.getLocationPOIs(index);
      pois.forEach(poi => {
        if (poi.uuid) {
          this.updatePOIMarker(poi);
        }
      });
    });
  }

  // Load available maki icons
  loadMakiIcons(): void {
    // List of available maki icons (this could be dynamically loaded)
    this.availableMakiIcons = [
      'marker', 'star', 'heart', 'home', 'attraction', 'museum', 'art-gallery',
      'restaurant', 'cafe', 'bar', 'fast-food', 'bakery', 'ice-cream', 'lodging', 'campsite',
      'hospital', 'pharmacy', 'doctor', 'dentist',
      'bank', 'post', 'police', 'fire-station',
      'school', 'college', 'library',
      'park', 'playground', 'garden', 'beach', 'swimming', 'grocery', 'clothing-store', 'gift',
      'cinema', 'theatre', 'music', 'nightclub',
      'religious-christian', 'religious-jewish', 'religious-muslim', 'religious-shinto',
      'monument', 'castle', 'historic', 'viewpoint',
      'airport', 'bus', 'ferry', 'rail', 'parking',
      'fuel', 'car-repair', 'bicycle',
      'information', 'telephone', 'toilet', 'drinking-water'
    ];
  }

  // Cluster Management Methods (Obelisks & Quests)
  getLocationClusters(locationIndex: number): (QuestMetadata | Obelisk)[] {
    const location = this.getLocationFormGroup(locationIndex);
    return location?.get('clusters')?.value || [];
  }

  openClusterSearch(locationIndex: number): void {
    this.selectedLocationIndex.set(locationIndex);
    this.showClusterModal.set(true);
    this.clusterSearchQuery = '';
    this.clusterSearchResults = [];
  }

  searchClusters(): void {
    // Push the search query to the Subject (will be debounced)
    this.clusterSearchSubject$$.next(this.clusterSearchQuery);
  }

  private performClusterSearch(query: string): void {
    if (!query || query.length < 2) {
      this.clusterSearchResults = [];
      this.searchingClusters.set(false);
      return;
    }

    this.searchingClusters.set(true);

    zip([
      this.questsService.loadQuests(
        null,
        query,
        true,
        null,
        null,
        {
          page: 1,
          pageSize: 20
        }
      ),
      this.explorationService.loadObelisks$([], null, query, {}, 20)
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.searchingClusters.set(false))
    ).subscribe(([quests, obelisks]) => {
      this.clusterSearchResults = [
        ...quests,
        ...obelisks
      ];
      this.clusterSearchResults.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  addClusterToLocation(cluster: QuestMetadata | Obelisk): void {
    const locationIndex = this.selectedLocationIndex();
    const location = this.getLocationFormGroup(locationIndex);
    const clusters = this.getLocationClusters(locationIndex);

    // Check if already added
    const exists = clusters.some(c =>
      (isQuestMetadata(c) && isQuestMetadata(cluster) && c.id === cluster.id) ||
      (isObelisk(c) && isObelisk(cluster) && c.uuid === cluster.uuid)
    );

    if (exists) {
      this.snackBar.open(
        this.transloco.translate('itemAlreadyAddedToLocation'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    const updatedClusters = [...clusters, cluster];
    location.patchValue({ clusters: updatedClusters });

    // Add marker to map
    this.addClusterMarker(cluster);

    // Close modal
    this.closeClusterModal();
  }

  removeClusterFromLocation(locationIndex: number, cluster: QuestMetadata | Obelisk): void {
    const location = this.getLocationFormGroup(locationIndex);
    const clusters = this.getLocationClusters(locationIndex);

    console.log('Removing cluster from location:', cluster);

    const updatedClusters = clusters.filter(c => {
      console.log('Checking cluster:', c);
      if (isQuestMetadata(c) && isQuestMetadata(cluster)) {
        return c.id !== cluster.id;
      }
      if (isObelisk(c) && isObelisk(cluster)) {
        return c.uuid !== cluster.uuid;
      }
      return true;
    });
    console.log('Updated clusters:', updatedClusters);

    // Get the clusters form control and update its value
    const clustersControl = location.get('clusters');
    if (clustersControl) {
      clustersControl.setValue(updatedClusters);
      clustersControl.markAsDirty();
      clustersControl.updateValueAndValidity();
    }

    // Remove marker from map
    const markerId = this.getClusterMarkerId(cluster);
    const marker = this.clusterMarkers.get(markerId);
    if (marker) {
      marker.remove();
      this.clusterMarkers.delete(markerId);
    }
  }

  closeClusterModal(): void {
    this.showClusterModal.set(false);
    this.clusterSearchQuery = '';
    this.clusterSearchResults = [];
  }

  getClusterMarkerId(cluster: QuestMetadata | Obelisk): string {
    if (isQuestMetadata(cluster)) {
      return `quest-${cluster.id}`;
    } else {
      return `obelisk-${cluster.uuid}`;
    }
  }

  addClusterMarker(cluster: QuestMetadata | Obelisk): void {
    if (!this.map) return;

    const markerId = this.getClusterMarkerId(cluster);

    // Remove existing marker if it exists
    const existingMarker = this.clusterMarkers.get(markerId);
    if (existingMarker) {
      existingMarker.remove();
    }

    // Determine coordinates and type
    let coords: { lat: number; lng: number };
    let isQuest = false;
    let name = '';
    let description = '';

    if (isQuestMetadata(cluster)) {
      isQuest = true;
      name = cluster.name;
      description = cluster.description?.join(' ') || '';
      // Quest should have start location
      if (cluster.locationPoint) {
        coords = cluster.locationPoint;
      } else {
        console.warn('Quest has no start location:', cluster);
        return;
      }
    } else {
      name = cluster.name;
      description = cluster.description || '';
      coords = cluster.location;
    }

    // Create custom marker element
    const el = document.createElement('div');
    el.className = isQuest ? 'cluster-marker quest-marker' : 'cluster-marker obelisk-marker';
    el.style.width = '36px';
    el.style.height = '36px';
    el.style.cursor = 'pointer';

    // Use appropriate icon
    if (isQuest) {
      el.style.backgroundImage = "url('assets/project-icons/map.svg')";
    } else {
      el.style.backgroundImage = "url('assets/project-icons/place.svg')";
    }
    el.style.backgroundSize = '24px 24px';
    el.style.backgroundPosition = 'center';
    el.style.backgroundRepeat = 'no-repeat';

    // Create popup
    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
      <div style="padding: 8px;">
        <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: 600;">${name}</h3>
        <p style="margin: 4px 0; font-size: 11px; color: #666;">${isQuest ? 'Quest' : 'Obelisk'}</p>
        ${description ? `<p style="margin: 4px 0; font-size: 12px;">${description.substring(0, 100)}...</p>` : ''}
      </div>
    `);

    // Create marker
    const marker = new mapboxgl.Marker(el)
      .setLngLat([coords.lng, coords.lat])
      .setPopup(popup)
      .addTo(this.map);

    this.clusterMarkers.set(markerId, marker);
  }

  // Initialize all cluster markers on map load
  initializeAllClusterMarkers(): void {
    this.locations.controls.forEach((location, index) => {
      const clusters = this.getLocationClusters(index);
      clusters.forEach(cluster => {
        this.addClusterMarker(cluster);
      });
    });
  }

  removeImageByUuid(locationIndex: number, sectionIndex: number, uuid: string): void {
    const images = this.getSectionImages(locationIndex, sectionIndex);
    const imageIndex = images.controls.findIndex(img => img.value.uuid === uuid);

    if (imageIndex !== -1) {
      images.removeAt(imageIndex);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clusterSearchSubject$$.complete();
    this.destroy();
  }

  protected readonly isQuestMetadata = isQuestMetadata;
}
